import { MktWrapper } from '@playbooks/components/mkt/mkt-wrapper';
import { DisplaySection, DisplaySectionBody } from '@playbooks/molecules/panels';
import { Link } from '@playbooks/ui/components/link';

import { LegalHero } from 'mkt/legal/legal-hero';
import { formatDate } from 'utils';

const MktTermsRoute = ({ app, ssr, workspace, router, search, session, store, toast }) => {
	// Components
	const SupportEmail = () => (
		<Link variant='link' href='mailto:support@playbooks.ai' className='text-foreground hover:underline'>
			support@playbooks.ai
		</Link>
	);

	// Render
	return (
		<>
			<LegalHero title='Terms of Service' subtitle={`Last updated: ${formatDate('08/24/2026', 'MMMM Do, YYYY')}`} />
			<div className='mx-auto px-3 max-w-xxl my-4'>
				<DisplaySection className='py-8'>
					<DisplaySectionBody className='py-3 border-b space-y-4 pb-8 mb-8'>
						<h2 className='type-section-title'>Introduction</h2>
						<p className='text-sm text-foreground-body'>
							Playbooks (“Playbooks”, “we”, “our”, or “us”) provides the software template marketplace located at
							[www.playbooks.ai], including all of its subdomains, webpages, and subpages (the “Website”). These Terms
							of Service (these “Terms”) are a legal agreement between you (“you”, “your,” or “User”) and Playbooks
							governing your access to and use of the Website, and the applications, software, products, services, and
							Content (defined below) that we make available (collectively, the “Services”). For information regarding
							how Playbooks uses your information, please see our Privacy Policy.
						</p>

						<p className='text-sm text-foreground-body'>
							You certify that you are of legal age to form a binding contract and are not a person barred from
							receiving services under the laws of the United States or other applicable jurisdictions. If you are
							accepting these Terms on behalf of an enterprise or other legal entity, you represent that you have the
							authority to bind such enterprise to these Terms, and in such case, the term “User” shall apply to the
							enterprise. To register an Account with Playbooks, you must be 13 years or over. If you are between the
							ages of 13 and 18, you will only use the Website under the supervision of a parent or legal guardian who
							agrees to be bound by these Terms.
						</p>

						<p className='text-sm text-foreground-body'>
							BY CREATING AN ACCOUNT, VIEWING, UPLOADING OR LICENSING CONTENT, OR OTHERWISE USING OR VISITING THIS
							WEBSITE, YOU AGREE TO BE BOUND BY THESE TERMS. IF YOU DO NOT AGREE TO THESE TERMS, YOU MAY NOT ACCESS OR
							USE THE WEBSITE.
						</p>

						<p className='text-sm text-foreground-body'>
							PLEASE READ THESE TERMS CAREFULLY BEFORE USING THE SERVICES, AS THESE TERMS AFFECT YOUR LEGAL RIGHTS AND
							INCLUDE A CLASS ACTION WAIVER. SEE SUBSECTION 15 BELOW FOR MORE INFORMATION.
						</p>
					</DisplaySectionBody>

					<DisplaySectionBody className='py-3 border-b space-y-4 pb-8 mb-8'>
						<h2 className='type-section-title'>1. Services</h2>
						<p className='text-sm text-foreground-body'>
							Through our Website, we offer Users the ability to upload, store, and license their developed software
							(such Users are “Creators”) to Users who are looking to license and use a Creators software templates
							(such Users are “Licensees”). As part of the Services, Creators can distribute and monetize their software
							templates (the “Templates”) and Licensees can license Templates pursuant to the license restrictions
							applicable to the subject Template to jumpstart their software development projects. Users can also view
							all resources, software, information, applications, data, and other materials featured on the Website or
							that we otherwise make available through the Services (collectively, “Content”).
						</p>

						<h3 id='third-party-connectors' className='type-heading scroll-mt-6'>
							Third-Party Connectors
						</h3>
						<p className='text-sm text-foreground-body'>
							The Services may allow you to connect accounts, credentials, applications, data, or services supplied by
							third-party providers (“Third-Party Connectors”). Third-Party Connectors are governed by separate
							agreements between you and the applicable provider. By connecting a Third-Party Connector, you represent
							that you have authority to connect the account and authorize Playbooks to access and process provider data
							as necessary to provide the requested Services.
						</p>
						<p className='text-sm text-foreground-body'>
							You are solely responsible for obtaining and maintaining all required provider accounts, permissions,
							licenses, and consents, and for complying with provider terms, attribution and display requirements,
							permitted-use restrictions, retention and deletion requirements, privacy obligations, applicable laws, and
							the operation of your applications and services made available to downstream users. Connecting or making a
							Third-Party Connector available through Playbooks does not grant you additional rights to use, display,
							store, modify, or redistribute provider data.
						</p>
						<p className='text-sm text-foreground-body'>
							The availability of a Third-Party Connector does not constitute Playbooks’ endorsement of the provider, a
							warranty regarding the provider or its data, or confirmation that your intended use is permitted.
							Playbooks may limit, suspend, or disable access to a Third-Party Connector when reasonably necessary for
							security, provider-policy, operational, or legal reasons.
						</p>
					</DisplaySectionBody>

					<DisplaySectionBody className='py-3 border-b space-y-4 pb-8 mb-8'>
						<h2 className='type-section-title'>2. Accounts and Registration</h2>
						<p className='text-sm text-foreground-body'>
							a. If you want to access and use certain features of the Website, you will need to create a user account
							(your “Account”). To do so, we need an email address and unique username. As well, certain features may
							require that you include valid payment method in your Account registration. Please make sure you safeguard
							your password because you will be responsible for any activities or actions taken under your Account,
							whether or not you have authorized such activities or actions. You agree that the information that you
							provide to Playbooks about yourself upon registration of your Account is correct, current, and complete,
							and you further agree that you will promptly update this information to ensure that it remains correct,
							current, and complete. For security reasons, we may require that you change your password from time to
							time. If you become aware of any use of your Account by someone other than you, please let us know by
							emailing <SupportEmail />. If you do not use your full name as your username for your Account, you may not
							use any term that is offensive, vulgar, infringes on a third party’s intellectual property rights, or is
							otherwise in violation of these Terms. As well, your username should not impersonate another User.
						</p>

						<p className='text-sm text-foreground-body'>
							b. If you are a Creator or a Licensee, you will need to create an Account because the Templates that are
							uploaded by Creators or licensed by Licensee’s will appear on your Account. You acknowledge and agree that
							all Content that is contained within your Account will only be available while your Account is active. If
							we terminate your use of the Services, we won’t have any obligation to store any Content in your Account.
							In short – please make sure you create a backup of all Content stored in your Account.
						</p>
					</DisplaySectionBody>

					<DisplaySectionBody className='py-3 border-b space-y-4 pb-8 mb-8'>
						<h2 className='type-section-title'>3. User-Generated Content</h2>

						<p className='font-normal text-foreground-body'>a. Submission of user-generate content.</p>
						<p className='text-sm text-foreground-body'>
							As a User, you may create, post, or upload Content on or through the Website (“User-Generated Content”).
							Posting any User-Generated Content is entirely voluntary, and you will be solely responsible for all
							User-Generated Content, including your Templates, that you submit to the Website (collectively, “Your
							Content”), including the consequences of our posting or publishing Your Content. We are not responsible
							for any public display or misuse of your User-Generated Content by other Users.
						</p>

						<p className='font-normal text-foreground-body'>b. Representations and Warranties.</p>
						<p className='text-sm text-foreground-body'>
							You represent and warrant that the use of your User-Generated Content by Playbooks or any other user of
							the Services in accordance with these Terms does not violate the rights of any third party or any laws,
							rules, or regulations.
						</p>

						<p className='font-normal text-foreground-body'>c. Restrictions.</p>
						<p className='text-sm text-foreground-body'>
							You agree that you will not distribute any User-Generated Content that:
						</p>
						<ul className='list-decimal pl-8 py-4'>
							<li className='gap-4 py-2 pl-4'>
								is defamatory, abusive, harassing, threatening, or an invasion of a right of privacy of another person;
								is bigoted, hateful, or racially or otherwise offensive; is violent, vulgar, obscene, pornographic or
								otherwise sexually explicit; or otherwise harms or can reasonably be expected to harm any person or
								entity;
							</li>

							<li className='gap-4 py-2 pl-4'>
								is illegal or encourages or advocates illegal activity or the discussion of illegal activities with the
								intent to commit them;
							</li>

							<li className='gap-4 py-2 pl-4'>
								infringes or violates any right of a third party including any copyright, patent, trademark, trade
								secret or other proprietary or contractual rights, right of privacy (specifically, you must not
								distribute another person’s personal information of any kind without their express permission) or
								publicity, or any confidentiality obligation;
							</li>

							<li className='gap-4 py-2 pl-4'>
								contains a virus or other harmful component, or otherwise tampers with, impairs or damages the Site or
								any connected network, or otherwise interferes with any person or entity’s use or enjoyment of the Site;
								or
							</li>

							<li className='gap-4 py-2 pl-4'>
								is antisocial, disruptive, or destructive, including “flaming,” “spamming,” “flooding,” “trolling,” and
								“griefing,” as those terms are commonly understood and used on the Internet.
							</li>
						</ul>

						<p className='font-normal text-foreground-body'>d. License to Playbooks of Your Content.</p>
						<p className='text-sm text-foreground-body'>
							You acknowledge that Playbooks requires certain rights for Playbooks it to provide the Services to you and
							to promote Playbooks, the Website, and the Services. By creating, posting, or sharing Your Content on or
							through our Services, you grant Playbooks a non-exclusive, worldwide, transferable, sub-licensable right
							and license to publicly display, store, archive, and share Your Content for purposes of providing the
							Services to you and making Your Content available to other Users as concontainerd by these Terms. You may
							remove Your Content from the Website at any time. We reserve the right to refuse to accept, post, display,
							or transmit any of your User-Generated Content in our sole discretion.
						</p>

						<p className='font-normal text-foreground-body'>e. License to Other Users of Your Content.</p>
						<p className='text-sm text-foreground-body'>
							By posting Your Content to the Website, you acknowledge that it can be viewed by any other User of the
							Website. Other than as to Templates, which are subject to the terms below, you hereby grant to each User a
							nonexclusive, worldwide license to use, display, and perform Your Content through the Service.
						</p>

						<p className='font-normal text-foreground-body'>f. Disclaimer of All User-Generated Content.</p>
						<p className='text-sm text-foreground-body'>
							We do not endorse any User-Generated Content and expressly disclaims any and all liability in connection
							with such User-Generated Content. We reserve the right (but we are not obligated) to review any
							User-Generated Content, investigate, or take appropriate action against you if you violate these
							restrictions or otherwise create liability for Playbooks or any other person. Such acts may include
							removing or modifying your User-Generated Content, terminating your Account, or reporting you to law
							enforcement authorities.
						</p>

						<p className='font-normal text-foreground-body'>g. Feedback.</p>
						<p className='text-sm text-foreground-body'>
							Playbooks was created so that software Creators could come together to share ideas and in that spirit,
							Playbooks is happy to hear all suggestions, feedback, or ideas related to the Website, the Services, and
							how Playbooks can improve them. If you provide Playbooks with any feedback, you grant to Playbooks a
							non-exclusive, worldwide, royalty-free, irrevocable, sub-licensable, perpetual license to use and publish
							those ideas and materials for any purpose, without compensation to you.
						</p>
					</DisplaySectionBody>

					<DisplaySectionBody className='py-3 border-b space-y-4 pb-8 mb-8'>
						<h2 className='type-section-title'>4. Posting and Licensing of Templates</h2>
						<p className='text-sm text-foreground-body'>
							a. Only Users who are designated as a “Creator” on the Website will be able to upload Templates to the
							Website. For information on how you can receive a Creator designation, see our please see our FAQ page.
							Creators will submit Templates to Playbooks for its review prior to having the right to upload it to the
							Website. The purpose of the Website is to make Templates publicly available, so you acknowledge that all
							Templates are public, and that the Website does currently not currently allow for private storage of
							Templates – if we do in the future, you’ll be the first to know. Playbooks reserves the right to reject
							any Template for any reason. Further, if you breach these Terms, Playbooks reserves the right to remove
							such User’s “Creator” status upon written notice to such User. A User can be both a Creator and a Licensee
							with regards to the Content.
						</p>

						<p className='text-sm text-foreground-body'>
							b. As a Creator, you acknowledge that each Template that is posted to your Account will be publicly
							accessible by all Users, whether or not they are Licensees. That means that Users can review your
							Templates prior to licensing them. Creators must adopt a license pursuant to which each Template can be
							licensed by a Licensee. For information on how to adopt a license, please see our FAQ page. Each license
							will be displayed with the Template so that Licensees know how the Template can be used when they license
							it. Creators shall be permitted to establish the fees applicable to the Templates it licenses to Users,
							subject to policies and guidelines that Playbooks may promulgate from time to time.
						</p>

						<p className='text-sm text-foreground-body'>
							c. Creator represents and warrants that it has all rights, title, and interest, in and to the Templates in
							order to license such Templates to Licensees via the Website. If a Template includes software that is
							subject to a sublicense, Creator agrees that its use of such software as part of its Template shall be in
							accordance with its sublicence.
						</p>

						<p className='text-sm text-foreground-body'>
							d. Playbooks’ review of the Templates is for its own internal purposes and the right to post a Template to
							the Website does not create any warranty related to the quality of the Template or the validity of the
							license under which the Template may be used.
						</p>

						<p className='text-sm text-foreground-body'>
							e. As a Licensee, User agrees to use each Template strictly in accordance with such Template’s license.
							Any User can report another User for misuse of a Template by prospecting Playbooks at <SupportEmail />.
						</p>
					</DisplaySectionBody>

					<DisplaySectionBody className='py-3 border-b space-y-4 pb-8 mb-8'>
						<h2 className='type-section-title'>5. Payments and Creator Revenue Share</h2>

						<p className='font-normal text-foreground-body'>a. Fees.</p>
						<p className='text-sm text-foreground-body'>
							Use of the Website is free, however, Templates may be subject to license fees. Fees will be as set forth
							on the applicable subpage for the Template, and are typically set by the Creator posting the Template, and
							are subject to change at any time. All amounts owed by you will be provided in an order summary on the
							checkout page and are immediately due and payable, and all fees must be paid in full prior to receiving
							the right to download a Template. Fees are non-refundable except where the User can show that Playbooks
							breached its obligations under these Terms. All fees will be paid in U.S. dollars, and are exclusive of
							all taxes, levies, and duties imposed by taxing authorities. You are solely responsible for payment of all
							such taxes, levies, and duties, excluding only U.S. taxes based solely on Playbooks’ income.
						</p>

						<p className='font-normal text-foreground-body'>b. Billing Information.</p>
						<p className='text-sm text-foreground-body'>
							You agree to provide to Playbooks accurate and complete billing information including legal name, address,
							telephone number, and credit card or debit card billing information. All fees will be billed to such
							credit or debit card. Playbooks is not responsible for any charges or expenses (e.g., for overdrawn
							accounts, exceeding credit card limits, etc.) resulting from charges billed by Playbooks. Each charge will
							be considered valid unless disputed by you in writing within thirty (30) days after the billing date. No
							adjustments will be made for disputed charges made more than thirty (30) days after the billing date.
						</p>

						<p className='font-normal text-foreground-body'>c. Template Revenue Share</p>
						<ul className='list-decimal pl-8 py-4'>
							<li className='gap-4 py-2 pl-4'>
								Creators are entitled to a percentage of the revenues received by Playbooks from fees received for
								license of the Creator’s Templates (the “Revenue Share”). For each paid Template licensed, Playbooks
								will remit to Creator the Revenue Share pursuant to the [Revenue Share Plan]. For each month that
								Creator’s Account is active, the Revenue Share shall be calculated for all Template license fees
								received by Playbooks for Creator’s Templates in the preceding month and Playbooks will remit such
								Revenue Share together with a report as to the licensed Templates.
							</li>

							<li className='gap-4 py-2 pl-4'>
								If Creator disagrees with the calculation of the Revenue Share, Creator shall provide written notice of
								the same within thirty (30) days of receipt of the Revenue Share payment. Creator waives all disputes as
								to calculation of the Revenue Share if it does not timely notify Playbooks of such dispute. Creator
								shall be responsible for payment of all taxes related to or arising from receipt of the Revenue Share
								payments. Creator and Playbooks will set up a direct deposit account with Playbooks to which Playbooks
								can remit the Revenue Share payments.
							</li>

							<li className='gap-4 py-2 pl-4'>
								We may withhold Revenue Share payments or offset accrued Revenue Share payments if Creator is in breach
								of these Terms or if a Licensee has contested payment of any fees for Creator’s Templates. Playbooks
								reserves the right to modify the Revenue Share, and any obligations to remit the Revenue Share, from
								time to time, at its sole discretion, and upon reasonable prior notice to Creator.
							</li>
						</ul>
					</DisplaySectionBody>

					<DisplaySectionBody className='py-3 border-b space-y-4 pb-8 mb-8'>
						<h2 className='type-section-title'>6.Disclaimers of Warranty</h2>
						<p className='text-sm text-foreground-body'>
							Playbooks reserves the right at any time and from time to time to modify or discontinue, temporarily or
							permanently, the Website (or any part thereof) with or without notice. THE WEBSITE AND SERVICES ARE
							PROVIDED ON AN “AS IS” AND “AS AVAILABLE” BASIS AND WITHOUT WARRANTIES OF ANY KIND. PLAYBOOKS EXPRESSLY
							DISCLAIMS ALL WARRANTIES, WHETHER EXPRESS OR IMPLIED, WITH RESPECT TO THE WEBSITE, INCLUDING, BUT NOT
							LIMITED TO, IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NON-INFRINGEMENT.
							NO ADVICE OR INFORMATION GIVEN BY PLAYBOOKS, ITS EMPLOYEES OR AFFILIATES SHALL CREATE A WARRANTY. YOUR USE
							OF THE SERVICES IS AT YOUR OWN RISK.
						</p>
					</DisplaySectionBody>

					<DisplaySectionBody className='py-3 border-b space-y-4 pb-8 mb-8'>
						<h2 className='type-section-title'>7. Ownership and Intellectual Property</h2>

						<p className='font-normal text-foreground-body'>a. Rights in the Website</p>
						<p className='text-sm text-foreground-body'>
							You acknowledge that all intellectual property rights, including without limitation copyrights, patents,
							trademarks, and trade secrets in the Website are owned by Playbooks and our affiliates, licensors, and
							vendors. Your access to or use of the Services does not transfer to you or any third party any rights,
							title, or interest in or to such intellectual property rights. Playbooks and our affiliates and licensors
							and vendors reserve all rights not granted in these Terms. You may not modify the Website, create
							derivative works of the Website, or reverse engineer, reverse compile, reverse assemble or do any other
							operation with the Website that would reveal any source code, trade secrets, know-how or other proprietary
							information. Nothing in these Terms shall be construed or interpreted as granting or providing rights to
							you to use, reproduce, modify, distribute, perform, display, possess or control the source code or any
							other aspect of the Website. You may not remove or modify any notice of confidentiality, trade secret,
							trademark or copyright encoded, embodied, or displayed in connection the Website.
						</p>

						<p className='font-normal text-foreground-body'>b. Rights and Licenses to the Content</p>
						<p className='text-sm text-foreground-body'>
							You agree that you will not copy, duplicate, distribute, modify, publicly display, download, republish,
							store, send, or create derivate works from any material on the Website, including without limitation any
							Content, without Playbooks’ written consent. These rights and licenses do not apply to User-Generated
							Content (including Templates), which are subject to licenses and rights as set forth above. Except as to
							User-Generated Content, Playbooks retains full and complete title to all Content, including any
							downloadable software and all data that accompanies it.
						</p>

						<p className='font-normal text-foreground-body'>c. Termination of Rights and Licenses</p>
						<p className='text-sm text-foreground-body'>
							You may use the Website only while these Terms remain in effect. Under no circumstances shall you have any
							rights of any kind in or to the Website after any termination or expiration of your agreement to these
							Terms for any reason.
						</p>
					</DisplaySectionBody>

					<DisplaySectionBody className='py-3 border-b space-y-4 pb-8 mb-8'>
						<h2 className='type-section-title'>8. Indemnity</h2>
						<p className='text-sm text-foreground-body'>
							You shall indemnify, defend, and hold Playbooks and its affiliates, and each of their officers, directors,
							employees, agents, partners, and licensors, harmless from and against any claim, demand, loss, damage,
							cost, liability and expense, including, but not limited to, reasonable attorneys’ fees, resulting from or
							arising out of your: (a) violation of these Terms or any law or regulation; or (b) violation of any rights
							of another party. Playbooks will use reasonable efforts to notify you of any such claim, action or
							proceeding upon becoming aware of it. Playbooks shall give you full control of the defense of any claims
							for which Playbooks seeks indemnification and shall reasonably assist you, at your expense, in defense of
							such claims. If you fail to defend any such claims, Playbooks reserves the right, at your expense, to
							assume the exclusive defense and control of any matter for which you are required to indemnify Playbooks
							and you agree to cooperate with our defense of such claims. You agree not to settle any such claim without
							Playbooks’ prior written consent.
						</p>
					</DisplaySectionBody>

					<DisplaySectionBody className='py-3 border-b space-y-4 pb-8 mb-8'>
						<h2 className='type-section-title'>9. Limitation of Liability</h2>
						<p className='text-sm text-foreground-body'>
							YOU AGREE THAT, TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, UNDER NO CIRCUMSTANCES SHALL PLAYBOOKS
							BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL OR CONSEQUENTIAL DAMAGES, INCLUDING, BUT NOT LIMITED TO,
							DAMAGES FOR LOSS OF PROFITS, USE, IMAGES, DATA OR OTHER INTANGIBLES, EVEN IF PLAYBOOKS HAS BEEN ADVISED OF
							THE POSSIBILITY OF SUCH DAMAGES, THAT RESULT FROM THE USE OR THE INABILITY TO USE THE WEBSITE, FROM ANY
							CHANGES TO THE WEBSITE, OR FROM UNAUTHORIZED ACCESS TO OR ALTERATION OF YOUR TRANSMISSIONS. YOU
							SPECIFICALLY ACKNOWLEDGE THAT DOWN-TIME AND COMPUTER VIRUSES ARE RISKS INHERENT IN THE USE OF THE INTERNET
							AND SOFTWARE PRODUCTS, AND YOU AGREE TO ASSUME RESPONSIBILITY FOR ANY HARM OR DAMAGES OF ANY KIND OR
							CHARACTER WHATSOEVER RESULTING FROM THESE POSSIBLE HARMS.
						</p>

						<p className='text-sm text-foreground-body'>
							IF YOU ARE DISSATISFIED WITH THE WEBSITE OR WITH ANY OF THESE TERMS, OR FEEL PLAYBOOKS HAS BREACHED THESE
							TERMS, YOUR SOLE AND EXCLUSIVE REMEDY IS TO DISCONTINUE USING THE WEBSITE. THE TOTAL LIABILITY OF
							PLAYBOOKS TO YOU FOR ANY CLAIM ARISING FROM OR RELATING TO THESE TERMS OR USE OF SERVICES SHALL NOT EXCEED
							THE AMOUNT PAID BY YOU FOR THE SERVICES DURING THE TWELVE MONTHS PRECEDING THE EVENT GIVING RISE TO THE
							LIABILITY. IT IS THE INTENTION OF YOU AND PLAYBOOKS THAT THIS PROVISION BE CONSTRUED BY A COURT AS BEING
							THE BROADEST LIMITATION OF LIABILITY CONSISTENT WITH APPLICABLE LAW.
						</p>

						<p className='text-sm text-foreground-body'>
							SOME JURISDICTIONS DO NOT ALLOW THE LIMITATION OR EXCLUSION OF INCIDENTAL, CONSEQUENTIAL OR OTHER TYPES OF
							DAMAGES, SO SOME OF THE ABOVE LIMITATIONS MAY NOT APPLY TO YOU. NOTHING IN THESE TERMS AND IN PARTICULAR
							WITHIN THIS SECTION 9 SHALL ATTEMPT TO EXCLUDE LIABILITY THAT CANNOT BE EXCLUDED UNDER APPLICABLE LAW.
						</p>
					</DisplaySectionBody>

					<DisplaySectionBody className='py-3 border-b space-y-4 pb-8 mb-8'>
						<h2 className='type-section-title'>10. Suspension and Termination</h2>
						<p className='text-sm text-foreground-body'>
							We expect that all Users will police their own behavior and their use of the Website and Services. That
							said, if we believe that you may be in violation of these Terms (including any license obligations for a
							licensed Template), we reserve the right to suspend or terminate your Account and your access to and use
							of the Services with or without advanced notice. You agree that Playbooks shall not be liable to you or to
							any third party for any modification, suspension, or discontinuance of the Website or other Services.
						</p>
					</DisplaySectionBody>

					<DisplaySectionBody className='py-3 border-b space-y-4 pb-8 mb-8'>
						<h2 className='type-section-title'>11. Copyright Infringement</h2>

						<p className='text-sm text-foreground-body'>
							a. Playbooks provides a marketplace where individual Users can upload, market, and license to others their
							own software (or software that is licensed with the permission of the owner). Playbooks does not create
							the Templates that are licensed, nor does Playbooks license such Templates. All Templates are generated by
							the Creators whose names appear on the Template subpage, and all such Creators are all independent
							software Creators and coders who are unaffiliated with Playbooks. Each User is responsible for ensuring
							that they have all necessary rights to the Content they post to the Website and and they are not
							infringing or violating any third party’s rights by posting it. Although Playbooks cannot guarantee the
							intellectual property ownership of the User-Generated Content on the Website, Playbooks will remove
							material that is flagged as infringing on a party’s intellectual property rights if it is reported in
							accordance with this policy.
						</p>

						<p className='text-sm text-foreground-body'>
							b. Claims of Copyright Infringement. Playbooks has adopted and implemented a policy that provides for the
							termination, in appropriate circumstances as determined by us in our sole discretion, of users who are
							infringers of copyright. Pursuant to Title 17, United States Code, Section 512(c)(2), notifications of
							claimed copyright infringement must be sent to the service provider’s designated agent. Notification must
							be submitted to the following:
						</p>

						<code className='p-4 my-4 w-full'>
							<p className='text-sm text-foreground-body'>Playbooks, Inc</p>
							<p className='text-sm text-foreground-body'>2211 Vermont Ct, Vail, CO 81657</p>
							<p className='text-sm text-foreground-body'>support@playbooks.ai</p>
						</code>

						<p className='text-sm text-foreground-body'>
							c. Notice of Infringement. To be effective, the notification must be a written communication that includes
							the following:
						</p>
						<ul className='list-decimal pl-8 py-4'>
							<li className='gap-4 py-2 pl-4'>
								A physical or electronic signature of person authorized to act on behalf of the owner of an exclusive
								right that is allegedly infringed;
							</li>

							<li className='gap-4 py-2 pl-4'>
								Identification of the copyrighted work claimed to have been infringed, or, if multiple copyrighted works
								at a single online site are covered by a single notification, a representative list of such works at
								that site;
							</li>

							<li className='gap-4 py-2 pl-4'>
								Identification of the material that is claimed to be infringing or to be the subject of infringing
								activity and that is to be removed or access to which is to be disabled, and information reasonably
								sufficient to permit us to locate the material;
							</li>

							<li className='gap-4 py-2 pl-4'>
								Information reasonably sufficient to permit us to prospect the complaining party, such as an address,
								telephone number, and, if available, an electronic mail address at which the complaining party may be
								prospected;
							</li>

							<li className='gap-4 py-2 pl-4'>
								A statement that the complaining party has a good-faith belief that use of the material in the manner
								complained of is not authorized by the copyright owner, its agent, or the law; and
							</li>

							<li className='gap-4 py-2 pl-4'>
								A statement that the information in the notification is accurate, and under penalty of perjury, that the
								complaining party is authorized to act on behalf of the owner of an exclusive right that is allegedly
								infringed.
							</li>
						</ul>

						<p className='text-sm text-foreground-body'>
							d. Takedown Notices. We may give you notice that we have removed or disabled access to certain material by
							means of a general notice on our Website, email to the email address associated with your Account, or by
							written communication sent by first-class mail to your physical address in our records. If you receive
							such a notice, you may provide counter-notification in writing to the designated agent that includes the
							information below. To be effective, the counter-notification must be a written communication that includes
							the following:
						</p>
						<ul className='list-decimal pl-8 py-4'>
							<li className='gap-4 py-2 pl-4'>Your physical or electronic signature;</li>

							<li className='gap-4 py-2 pl-4'>
								Identification of the material that has been removed or to which access has been disabled, and the
								location at which the material appeared before it was removed or access to it was disabled;
							</li>

							<li className='gap-4 py-2 pl-4'>
								A statement from you under the penalty of perjury, that you have a good faith belief that the material
								was removed or disabled as a result of a mistake or misidentification of the material to be removed or
								disabled; and
							</li>

							<li className='gap-4 py-2 pl-4'>
								Your name, physical address and telephone number, and a statement that you consent to the jurisdiction
								of a U.S. federal district court for the judicial district in which your physical address is located, or
								if your physical address is outside of the United States, for any judicial district in which Playbooks
								may be found, and that you will accept service of process from the person who provided notification of
								allegedly infringing material or an agent of such person.
							</li>
						</ul>

						<p className='text-sm text-foreground-body'>
							e. Repeat Infringement. Playbooks will terminate the rights of Users to use the Services who are subject
							to repeat or multiple notices of intellectual property infringement at Playbooks’ sole discretion.
							Playbooks reserves the right to refuse access to the Services or the setting up of an Account by a User
							whose rights were previously terminated.
						</p>
					</DisplaySectionBody>

					{/* Where I stopped */}
					<DisplaySectionBody className='py-3 border-b space-y-4 pb-8 mb-8'>
						<h2 className='type-section-title'>12. Modification</h2>
						<p className='text-sm text-foreground-body'>
							Playbooks may modify these Terms at any time. Modifications become effective immediately upon your first
							access to or use of the Website after the “Last Revised” date at the top of these Terms. Your continued
							access to or use of the Website after the modifications have become effective will be deemed your
							conclusive acceptance of the modified Terms. If you do not agree with the modifications, do not access or
							use the Website.
						</p>
					</DisplaySectionBody>

					<DisplaySectionBody className='py-3 border-b space-y-4 pb-8 mb-8'>
						<h2 className='type-section-title'>13. Restricted Users</h2>
						<p className='text-sm text-foreground-body'>
							You may not create an Account if you are a member of a terror or hate group. You may not purchase any
							goods or services from us if you reside in a country subject to a comprehensive U.S. sanctions program or
							are a Specifically Designated National (SDN) as designated by the U.S. Department of the Treasury.
						</p>
					</DisplaySectionBody>

					<DisplaySectionBody className='py-3 border-b space-y-4 pb-8 mb-8'>
						<h2 className='type-section-title'>14. Governing Law</h2>
						<p className='text-sm text-foreground-body'>
							These Terms shall be governed by the laws of the State of Colorado without giving effect to any conflict
							of laws principles that may require the application of the law of another jurisdiction.
						</p>
					</DisplaySectionBody>

					<DisplaySectionBody className='py-3 border-b space-y-4 pb-8 mb-8'>
						<h2 className='type-section-title'>15. Dispute Resolution</h2>
						<p className='text-sm text-foreground-body'>
							Please read this section carefully, as it affects your rights. If you believe that Playbooks has not
							adhered to these Terms, please contact us by e-mail at <SupportEmail />. We will do our best to address
							your concerns. If you feel that your complaint has been addressed incompletely, we invite you to let us
							know for further investigation.
						</p>

						<p className='text-sm text-foreground-body'>
							a. If you and Playbooks are unable to reach a resolution to the dispute, you and Playbooks will settle the
							dispute exclusively under the rules of the American Arbitration Association (www.adr.org) at its Denver,
							CO office. Any election to arbitrate by one party will be final and binding on the other. If you wish to
							initiate arbitration, you must serve Playbooks with an arbitration demand by mail to [address or
							registered agent]. YOU UNDERSTAND THAT IF EITHER PARTY ELECTS TO ARBITRATE, NEITHER PARTY WILL HAVE THE
							RIGHT TO SUE IN COURT OR HAVE A JURY TRIAL. YOU AND PLAYBOOKS AGREE THAT ANY ARBITRATION WILL BE LIMITED
							TO THE DISPUTE BETWEEN PLAYBOOKS AND YOU INDIVIDUALLY. YOU ACKNOWLEDGE AND AGREE THAT YOU AND PLAYBOOKS
							ARE EACH WAIVING THE RIGHT TO PARTICIPATE AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS ACTION OR
							REPRESENTATIVE PROCEEDING.
						</p>

						<p className='text-sm text-foreground-body'>
							b. Further, unless both you and Playbooks otherwise agree in writing, an arbitrator may not consolidate
							more than one person’s claims and may not otherwise preside over any form of any class or representative
							proceeding. Regardless of any statute or law to the contrary, any claim or cause of action arising out of
							or related to the use of the Services or these Terms must be brought, if at all, within one year from the
							accrual of the claim or cause of action or be forever barred.
						</p>

						<p className='text-sm text-foreground-body'>
							c. Notwithstanding the foregoing, Playbooks may seek injunctive or other equitable relief to protect its
							confidential information and intellectual property rights or to prevent loss of data or damage to its
							servers in any court of competent jurisdiction.
						</p>

						<p className='text-sm text-foreground-body'>
							d. If you are subject to any state or federal law requiring that the laws of a different jurisdiction
							apply or that prohibit dispute resolution in the manner required in this Section, then this Section shall
							be deemed revised to conform with such laws.
						</p>

						<p className='text-sm text-foreground-body'>
							e. You have a limited right to opt out of this arbitration obligation. If you are a new User, you can opt
							out of this arbitration obligation within 30 days of first accepting these Terms. If you are an existing
							User, you can opt out within 30 days after the Last Revised date of these Terms. To opt out, you must send
							a timely email to <SupportEmail /> with your information and a request to opt out of these arbitration
							terms. If you validly opt out, you will not need to keep opting out when these Terms are updated, and your
							initial opt out request will continue to be honored.
						</p>
					</DisplaySectionBody>

					<DisplaySectionBody className='py-3 border-b space-y-4 pb-8 mb-8'>
						<h2 className='type-section-title'>16. Disputes with Other Users</h2>
						<p className='text-sm text-foreground-body'>
							If you have a dispute with another User, we encourage you to prospect the other party to try to resolve
							the dispute amicably. We are building a community of developers at Playbooks and we will try to help where
							we can, but you acknowledge that Playbooks has no obligation to resolve, or to assist in resolving, any
							disputes. As well, you release Playbooks from any claims, demands, and damages arising out of disputes
							with other Users or any third parties.
						</p>
					</DisplaySectionBody>

					<DisplaySectionBody className='py-3 border-b space-y-4 pb-8 mb-8'>
						<h2 className='type-section-title'>17. Compliance with Laws</h2>
						<p className='text-sm text-foreground-body'>
							You acknowledge that the Website is not specifically designed to facilitate compliance with any specific
							law, rule, or regulation. Your use of the Website in compliance with any specific law, rule, or regulation
							applicable to you is your sole responsibility. Playbooks is not responsible for enabling your compliance
							with any such law, rule, or regulation or for your failure to comply. You represent and warrant to
							Playbooks that your use of and access to the Website will comply with all applicable laws, rules, and
							regulations and will not cause Playbooks or any of its affiliates, licensors, or vendors to violate any
							applicable laws, rules, and regulations.
						</p>
					</DisplaySectionBody>

					<DisplaySectionBody className='py-3 border-b space-y-4 pb-8 mb-8'>
						<h2 className='type-section-title'>18. International Use</h2>
						<p className='text-sm text-foreground-body'>
							Playbooks controls and operates the Website from the state of Colorado. Playbooks does not represent that
							the Website or any materials on the Website are appropriate or available for use in other locations
							outside of the United States. Persons who choose to access the Website from other locations do so on their
							own initiative, and are responsible for compliance with local laws, if and to the extent local laws are
							applicable. Without limiting the generality of the foregoing, you agree to comply with all applicable laws
							regarding the transmission or transfer of any data or technical information in connection with your access
							to or use of the Website.
						</p>
					</DisplaySectionBody>

					<DisplaySectionBody className='py-3 border-b space-y-4 pb-8 mb-8'>
						<h2 className='type-section-title'>19. Survival of Terms</h2>
						<p className='text-sm text-foreground-body'>
							The provisions of these Terms that are intended to survive the termination of these Terms shall so
							survive.
						</p>
					</DisplaySectionBody>

					<DisplaySectionBody className='py-3 border-b space-y-4 pb-8 mb-8'>
						<h2 className='type-section-title'>20. Prospect</h2>
						<p className='text-sm text-foreground-body'>
							Playbooks is located in Denver, CO. Any questions, comments, or suggestions, including any report of
							violation of these Terms should be provided to Playbooks as follows:
						</p>
						<p className='text-sm text-foreground-body'>
							<SupportEmail />
							<code className='p-4 my-4 w-full'>
								<span>Playbooks, Inc</span>
								<span>2627 Zenobia St, Denver, CO 80212</span>
								<span>support@playbooks.ai</span>
							</code>
						</p>
					</DisplaySectionBody>

					<DisplaySectionBody className='py-3 space-y-4 pb-8 mb-8'>
						<h2 className='type-section-title'>21. Entire Agreement</h2>
						<p className='text-sm text-foreground-body'>
							These Terms constitute the entire agreement between you and Playbooks with respect to the Website,
							superseding any prior agreements between you and Playbooks. The failure of Playbooks to exercise or
							enforce any right or provision of these Terms shall not constitute a waiver of such right or provision. If
							any provision of these Terms is found by a court of competent jurisdiction to be invalid, you and
							Playbooks nevertheless agree that the court should endeavor to give effect to the parties’ intentions as
							reflected in the provision, and the other provisions of these Terms shall remain in full force and effect.
							The parties hereto are independent parties, not agents, employees or employers of the other or joint
							venturers, and neither acquires hereunder any right or ability to bind or enter into any obligation on
							behalf of the other.
						</p>
					</DisplaySectionBody>
				</DisplaySection>
			</div>
		</>
	);
};

MktTermsRoute.getLayout = page => (
	<MktWrapper
		seo={{ description: 'Please checkout our terms of service to learn about your legal protections with Playbooks.' }}>
		{page}
	</MktWrapper>
);

export default MktTermsRoute;
